
import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Container,
  Heading,
  HStack,
  PinInput,
  PinInputField,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { apiJson, toUserMessage } from '../../lib/api';

export const MfaVerifyPage: React.FC = () => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { verifyMfa, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (code.length !== 6) return;

    setIsSubmitting(true);
    try {
      const { success, error } = await verifyMfa(code);
      if (success) {
        toast({
          title: 'Verification successful',
          status: 'success',
          duration: 3000,
        });
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const profile = session?.access_token
            ? await apiJson<{ globalRole: string }>('/api/auth/me', {}, session.access_token)
            : null;
          navigate(profile?.globalRole === 'ADMIN' ? '/admin' : '/home');
        } catch {
          // Only decides which landing page to use; verification already
          // succeeded, so fall back to the non-admin route rather than
          // showing the user an error for it.
          navigate('/home');
        }
      } else {
        setCode('');
        toast({
          title: "That code didn't work",
          description: error,
          status: 'error',
          duration: 6000,
        });
      }
    } catch (err) {
      toast({
        title: "That code didn't work",
        description: toUserMessage(err, 'Please request a new code and try again.'),
        status: 'error',
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Ask for a fresh code. Needed because the login-time challenge can fail
   * (slow email provider, rate limit) and AuthContext now fails closed —
   * without this the user would sit on this screen with no way to get a code
   * short of signing out and back in.
   */
  const handleResend = async () => {
    setIsResending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Your session has expired. Please sign in again.');
      }
      await apiJson('/api/auth/mfa/challenge', { method: 'POST' }, session.access_token);
      setCode('');
      toast({
        title: 'New code sent',
        description: 'Check your email for the new verification code.',
        status: 'success',
        duration: 4000,
      });
    } catch (err) {
      toast({
        title: "We couldn't send a new code",
        description: toUserMessage(err, 'Please try again in a moment.'),
        status: 'error',
        duration: 6000,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', md: '8' }}>
      <Box
        py={{ base: '0', md: '8' }}
        px={{ base: '4', md: '10' }}
        bg={{ base: 'transparent', md: 'bg-surface' }}
        boxShadow={{ base: 'none', md: 'sm' }}
        borderRadius="xl"
      >
        <VStack spacing="6">
          <VStack spacing="3" textAlign="center">
            <Heading size="lg">Two-Factor Authentication</Heading>
            <Text color="muted">
              We sent a verification code to your email. Please enter it below.
            </Text>
          </VStack>

          <Box as="form" onSubmit={handleVerify} width="100%">
            <VStack spacing="6">
              <HStack justify="center">
                <PinInput
                  otp
                  size="lg"
                  value={code}
                  onChange={setCode}
                  isDisabled={isSubmitting || isResending}
                >
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                </PinInput>
              </HStack>

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                fontSize="md"
                isLoading={isSubmitting}
                width="full"
                isDisabled={code.length !== 6}
              >
                Verify
              </Button>
            </VStack>
          </Box>

          <VStack spacing="2">
            <Button
              variant="link"
              colorScheme="blue"
              onClick={handleResend}
              isLoading={isResending}
              isDisabled={isSubmitting}
            >
              Didn't get a code? Send a new one
            </Button>
            <Button variant="link" colorScheme="gray" onClick={handleSignOut}>
              Back to Login
            </Button>
          </VStack>
        </VStack>
      </Box>
    </Container>
  );
};
